import React, { useEffect, useState } from 'react';
import ChevronIcon from 'assets/icons/arrow-down.svg';
import type { InventoryItemGroup } from 'features/shared/inventory/selections/utils/groupInventoryItems';
import 'features/shared/inventory/selections/components/InventorySelectionAccordionGroup.scss';

type InventorySelectionAccordionGroupProps<T> = {
  groups: InventoryItemGroup<T>[];
  renderRow: (item: T, index: number) => React.ReactNode;
  className?: string;
};

/**
 * Hand-rolled accordion (no `IonAccordionGroup`/`IonAccordion`) matching the
 * approved prototype's design exactly — card-bordered sections, a header row
 * with group name / item count / chevron, and an animated
 * `grid-template-rows` collapse. Used by both Home Selections (grouped by
 * `optionCategory`) and Home Colors (grouped by `colorGroupCode`).
 */
function InventorySelectionAccordionGroup<T>({
  groups,
  renderRow,
  className
}: InventorySelectionAccordionGroupProps<T>) {
  // All sections default-expanded per the approved prototype.
  const [openKeys, setOpenKeys] = useState<Set<string>>(
    () => new Set(groups.map((group) => group.key))
  );

  // Groups are recomputed from async-fetched/filtered items, so the set of
  // keys can change after the initial render (data loads, search narrows
  // results). Re-expand any newly-appeared section by resetting to "all
  // open" whenever the key set itself changes — but not on every render,
  // so a manual collapse survives re-renders that don't add/remove groups.
  const groupKeysSignature = groups.map((group) => group.key).join('|');
  useEffect(() => {
    setOpenKeys(new Set(groups.map((group) => group.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKeysSignature]);

  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div
      className={`hk-inventory-selection-accordion-group ${className ?? ''}`}
    >
      {groups.map((group) => {
        const expanded = openKeys.has(group.key);
        const isUngrouped = group.key === '__none__';
        const bodyId = `hk-inventory-selection-accordion-body-${group.key}`;
        return (
          <div
            key={group.key}
            className={`hk-inventory-selection-accordion-section${
              expanded ? ' expanded' : ''
            }${isUngrouped ? ' ungrouped' : ''}`}
          >
            <div
              className="hk-inventory-selection-accordion-header"
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              aria-controls={bodyId}
              onClick={() => toggle(group.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle(group.key);
                }
              }}
            >
              <span className="hk-inventory-selection-accordion-header-label">
                {group.label}
              </span>
              <span className="hk-inventory-selection-accordion-header-count">
                {group.items.length} item{group.items.length === 1 ? '' : 's'}
              </span>
              <img
                src={ChevronIcon}
                alt=""
                className="hk-inventory-selection-accordion-header-chevron"
              />
            </div>

            <div id={bodyId} className="hk-inventory-selection-accordion-body">
              <div className="hk-inventory-selection-accordion-body-inner">
                <div className="hk-inventory-selection-accordion-rows">
                  {group.items.map((item, index) => renderRow(item, index))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default InventorySelectionAccordionGroup;
